import { useEffect, useState } from 'react';
import { recordedAmount } from '../form/amountFields';
import { GroupMember } from '../grouping';
import { IncomeType } from '../hooks/useIncomes';
import { incomeTypeOptions } from '../incomeTypes';
import { conversionPreviewNote } from '../leftOut';
import { CashflowModalShell } from '../redesign/modal/CashflowModalShell';
import { Allocation, AmountField, sumAmounts } from '../redesign/modal/allocations';
import { IncomeRow } from '../rows';
import { CashflowFrequency, calculateAnnualAmount, cashflowFrequencyOptions } from '../utils';
import { incomeOwnersFrom, postTaxError, postTaxTotalError } from './jointAllocations';
import { incomeOwnersOf } from './owners';
import { useJointOwnership } from './useJointOwnership';
import { JointDeletion, JointIncomeSubmission } from './useJointSaves';

const EXTRA_FIELDS: AmountField[] = [{ key: 'amountPostTax', label: 'After tax' }];

type JointIncomeModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (submission: JointIncomeSubmission) => Promise<void>;
  members: GroupMember[];
  defaultClientId?: string;
  /** The client whose own tab the form was opened from; absent on the family tab. */
  viewingClientId?: string;
  initialRow?: IncomeRow;
  onDelete: (deletion: JointDeletion) => Promise<void>;
};

/**
 * An income is one amount before tax, held in shares by the people it applies to. Each person's
 * after-tax amount is their own, since tax depends on who receives the income.
 */
export function JointIncomeFormModal({
  opened,
  onClose,
  onSubmit,
  members,
  defaultClientId,
  viewingClientId,
  initialRow,
  onDelete,
}: JointIncomeModalProps) {
  const [incomeType, setIncomeType] = useState<IncomeType>('Employment');
  const [frequency, setFrequency] = useState<CashflowFrequency | ''>('Monthly');
  const [description, setDescription] = useState('');
  const [amountPreTax, setAmountPreTax] = useState<number | ''>('');

  const stored = initialRow?.items[0];
  const storedOwners = stored ? incomeOwnersOf(stored) : undefined;
  const ownership = useJointOwnership({
    opened,
    noun: 'income',
    storedId: stored?.id,
    storedOwners,
    members,
    defaultClientId,
    viewingClientId,
    onDelete,
  });
  const { allocations } = ownership;

  useEffect(() => {
    if (!opened) return;
    setIncomeType(initialRow?.incomeType ?? 'Employment');
    // Detected records can arrive without a frequency, so leave it for the adviser to choose
    setFrequency(initialRow ? (initialRow.frequency ?? '') : 'Monthly');
    setDescription(initialRow?.description ?? '');
    setAmountPreTax(initialRow?.amountPreTax ?? '');
  }, [opened, initialRow]);

  const chosenFrequency = frequency || null;
  const wholePreTax = recordedAmount(amountPreTax);
  const annual = (amount: number | null) => calculateAnnualAmount(amount, chosenFrequency);
  const postTaxTotal = postTaxTotalError(allocations, wholePreTax);
  const fieldError = (allocation: Allocation, key: string) =>
    postTaxError(allocation, key, wholePreTax);
  const viewerAllocation = allocations.find(allocation => allocation.clientId === viewingClientId);
  const viewerPostTax = recordedAmount(viewerAllocation?.amounts.amountPostTax);
  const summary = [
    {
      label: 'Annual income before tax',
      value: annual(wholePreTax),
      parts: ownership.shareParts(wholePreTax, annual),
    },
    {
      label: 'Annual income after tax',
      value: annual(sumAmounts(allocations, 'amountPostTax')),
      // An after-tax amount is a person's own, not a share, so only this client's is told apart
      parts: ownership.viewerName
        ? [{ label: ownership.viewerName, value: annual(viewerPostTax) }]
        : [],
    },
  ];
  const handleSubmit = () => {
    if (ownership.isSaving || !frequency) return;
    return ownership.save(() =>
      onSubmit({
        id: stored?.id,
        incomeType,
        frequency,
        description,
        amountPreTax: wholePreTax,
        owners: incomeOwnersFrom(allocations, storedOwners),
        storedClientIds: ownership.storedClientIds,
      }),
    );
  };

  return (
    <CashflowModalShell
      opened={opened}
      onClose={onClose}
      title={initialRow ? 'Edit income' : 'Add income'}
      submitLabel={initialRow ? 'Save income' : 'Add income'}
      onSubmit={handleSubmit}
      onFieldChange={ownership.clearRefusal}
      isSaving={ownership.isSaving}
      summaryNote={conversionPreviewNote(chosenFrequency)}
      notice={ownership.notice}
      summary={summary}
      testId="add-income-modal"
      fields={[
        [
          {
            kind: 'select',
            label: 'Type',
            value: incomeType,
            onChange: value => setIncomeType(value as IncomeType),
            data: incomeTypeOptions,
            required: true,
          },
          {
            kind: 'select',
            label: 'Frequency',
            value: frequency,
            onChange: value => setFrequency(value as CashflowFrequency),
            data: cashflowFrequencyOptions,
            required: true,
          },
        ],
        [
          { kind: 'text', label: 'Description', value: description, onChange: setDescription },
          {
            kind: 'money',
            label: 'Amount before tax',
            value: amountPreTax,
            onChange: setAmountPreTax,
          },
        ],
      ]}
      allocation={{
        ...ownership.allocationProps,
        share: { amountLabel: 'Before tax', of: wholePreTax, assumed: ownership.assumedShare },
        extraFields: EXTRA_FIELDS,
        error: postTaxTotal ?? ownership.allocationProps.error,
        fieldError,
      }}
      deletion={ownership.deletion}
    />
  );
}
